/**
 * The GitHub connection's state as Settings › Context describes it: title, status badge and message, and
 * `canLinkRepos` — GitHub returned at least one repository, which is exactly when the repo-linking section
 * (`WarehouseProjectsSection`) can offer one. The one rule for Settings, the setup panel's repo step and
 * the "Link repos" sheet (ACT2-1108), all rendered through `GithubConnectionCard`.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';

import type { Badge } from '@/_shared/components/ui/badge';

import { repoCountLabel } from '../components/repo-utils';

export type ListReposResult = Awaited<ReturnType<RouterClient<AppRouter>['settings']['scm']['listRepos']>>;

const LIST_FORMAT = new Intl.ListFormat('en', { style: 'long', type: 'conjunction' });

/** GitHub account names as a phrase: "acme", "acme and globex", or "acme, globex, and 3 more". */
function accountsLabel(logins: string[]): string {
  const accounts = [...new Set(logins)].sort((a, b) => a.localeCompare(b));
  if (accounts.length <= 3) return LIST_FORMAT.format(accounts);
  return LIST_FORMAT.format([...accounts.slice(0, 2), `${accounts.length - 2} more`]);
}

export interface ConnectionSummary {
  title: string;
  status: { variant: NonNullable<React.ComponentProps<typeof Badge>['variant']>; label: string };
  description: string;
  /** GitHub returned at least one repository, so a repo can be linked to a warehouse. */
  canLinkRepos: boolean;
}

export function summarizeGithubConnection(data: ListReposResult): ConnectionSummary {
  return { ...describeConnection(data), canLinkRepos: data.repos.length > 0 };
}

function describeConnection({
  repos,
  githubStatus,
  // The defaults cover a server that predates these fields. During a rolling deploy, a page can call a
  // pod that still runs the older server.
  installations = [],
  patListFailed = false,
  pendingInstallRequest = null,
}: ListReposResult): Omit<ConnectionSummary, 'canLinkRepos'> {
  const hasRepos = repos.length > 0;
  const connected = { variant: 'success', label: 'Connected' } as const;
  const empty = { variant: 'warning', label: 'No repositories' } as const;
  const failed = { variant: 'error', label: 'Error' } as const;

  if (githubStatus === 'app') {
    if (installations.length === 0) {
      return {
        title: 'GitHub App',
        status: pendingInstallRequest
          ? { variant: 'warning', label: 'Awaiting approval' }
          : { variant: 'warning', label: 'Not connected' },
        description:
          'Install the GitHub App and choose the repositories to link to a warehouse. If your GitHub admin already installed it, connect it instead.',
      };
    }
    // From the installations, not the repo owners: an installation whose repos GitHub did not return
    // has no repos in the list, and it must still show here.
    const installedOn = `Installed on ${accountsLabel(installations.map((installation) => installation.accountLogin))}`;
    const failedAccounts = installations
      .filter((installation) => installation.listFailed)
      .map((installation) => installation.accountLogin);
    if (failedAccounts.length > 0) {
      return {
        title: 'GitHub App',
        status: failed,
        description: `${installedOn} · Could not load the repositories for ${accountsLabel(failedAccounts)}`,
      };
    }
    return hasRepos
      ? { title: 'GitHub App', status: connected, description: `${installedOn} · ${repoCountLabel(repos.length)}` }
      : {
          title: 'GitHub App',
          status: empty,
          description: `${installedOn} · Configure the app to choose the repositories that you can link to a warehouse`,
        };
  }
  if (githubStatus === 'pat') {
    if (patListFailed) {
      return {
        title: 'Personal access token',
        status: failed,
        description: 'Could not load the repositories for the GitHub token configured for this deployment.',
      };
    }
    return {
      title: 'Personal access token',
      status: hasRepos ? connected : empty,
      description: hasRepos
        ? `Uses the GitHub token configured for this deployment · ${repoCountLabel(repos.length)}`
        : 'The GitHub token configured for this deployment returned no repositories.',
    };
  }
  return {
    title: 'GitHub App',
    status: { variant: 'outline', label: 'Not connected' },
    description: 'This deployment has no GitHub App or token configured.',
  };
}
